import {
    formatIsoTeeTimeUK,
    formatTournamentVsParDisplay,
    looksLikeIsoTimestamp,
    sumLinescoresRoundsVsPar,
} from "./espnRoundDetails";

function holeThruForGroups(c) {
    const dv = c.status?.displayValue;
    if (dv == null || dv === "") return "—";
    if (looksLikeIsoTimestamp(dv)) {
        return "—";
    }
    return dv;
}

function todayDetailForGroups(c) {
    const t = c.status?.todayDetail;
    if (t == null || t === "") return null;
    if (looksLikeIsoTimestamp(t)) return null;
    return t;
}

/** At least one hole scored in `competitionPeriod` (leaderboard linescores shape). */
function hasPlayedHoleInCurrentRound(c, competitionPeriod) {
    const rp = Number(competitionPeriod);
    if (!Number.isFinite(rp) || rp < 1) return false;
    const roundRow = (c.linescores || []).find(
        (ls) => Number(ls.period) === rp,
    );
    const holeList = roundRow?.linescores;
    if (!Array.isArray(holeList) || holeList.length === 0) return false;
    return holeList.some((h) => {
        const v = h?.displayValue;
        if (v == null) return false;
        const t = String(v).trim();
        return t !== "" && t !== "-" && t !== "—";
    });
}

function scoreDisplayForGroups(c) {
    const stat = (c.statistics || []).find((s) => s.name === "scoreToPar");
    if (stat?.displayValue) {
        const sv = String(stat.displayValue).trim();
        if (
            sv &&
            sv !== "--" &&
            !looksLikeIsoTimestamp(sv)
        ) {
            return sv;
        }
    }
    const sum = sumLinescoresRoundsVsPar(c.linescores);
    if (sum !== null) {
        return formatTournamentVsParDisplay(sum);
    }
    const s = c.score?.displayValue;
    if (s == null || s === "") return "—";
    if (looksLikeIsoTimestamp(s)) return "—";
    return s;
}

function competitorToPairingPlayer(c, competitionPeriod) {
    const rawToday = todayDetailForGroups(c);
    const playedHoleToday = hasPlayedHoleInCurrentRound(c, competitionPeriod);
    return {
        id: String(c.athlete?.id ?? c.id ?? ""),
        displayName: c.athlete?.displayName ?? "—",
        holeThru: holeThruForGroups(c),
        scoreDisplay: scoreDisplayForGroups(c),
        todayDetail: playedHoleToday ? rawToday : null,
        position: c.status?.position?.displayName ?? "",
        sortOrder: c.sortOrder ?? 0,
    };
}

/**
 * Tee groups from ESPN leaderboard: competitors who share `status.teeTime` for the current wave.
 * Cut players excluded. Missing tee times are grouped together.
 */
export function buildPairingGroups(competition) {
    if (!competition?.competitors?.length) return [];
    const rawPeriod = Number(competition.status?.period);
    const competitionPeriod =
        Number.isFinite(rawPeriod) && rawPeriod > 0 ? rawPeriod : 1;
    const competitors = competition.competitors.filter(
        (c) => c.status?.type?.name !== "STATUS_CUT",
    );
    const map = new Map();
    for (const c of competitors) {
        const tee = c.status?.teeTime;
        const key =
            typeof tee === "string" && tee.length > 0 ? tee : "__no_tee";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(c);
    }

    const groups = [];
    for (const [teeKey, list] of map) {
        const teeIso = teeKey === "__no_tee" ? null : teeKey;
        const sorted = [...list].sort(
            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
        );
        groups.push({
            teeTimeIso: teeIso,
            teeTimeLabel: teeIso ? formatIsoTeeTimeUK(teeIso) : "TBD",
            players: sorted.map((comp) =>
                competitorToPairingPlayer(comp, competitionPeriod),
            ),
        });
    }

    groups.sort((a, b) => {
        if (!a.teeTimeIso && !b.teeTimeIso) return 0;
        if (!a.teeTimeIso) return 1;
        if (!b.teeTimeIso) return -1;
        return new Date(b.teeTimeIso) - new Date(a.teeTimeIso);
    });

    return groups;
}
